import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';

const MAX_CANVAS_SIZE = 1200;
const ERASER_RADIUS_ADJUST = 1;
/** Brush paint dots (saved + in-progress) — green, independent of layer outline color. */
const BRUSH_STROKE_FILL = '#22c55e';
/** Fraction of brush/eraser radius: max distance between stored samples (overlap for solid stroke). */
const BRUSH_INTERPOLATE_STEP_FACTOR = 0.38;

function getToolRadius(size, tool) {
  const base = Math.max(1, Number(size ?? 10) / 2);
  if (tool === 'eraser') {
    // destination-out visually reads slightly larger; trim a bit to match brush feel.
    return Math.max(1, base - ERASER_RADIUS_ADJUST);
  }
  return base;
}

/**
 * Normalize polygon format to array of [x,y] points (outer ring only).
 * Handles: [[x,y],...], [[[x,y],...]], [x,y,x,y,...], { points, holes }
 */
function getPolygonPoints(poly) {
  const rings = getPolygonRings(poly);
  return rings[0] ?? [];
}

/**
 * Get polygon as array of rings [outer, hole1, hole2, ...].
 * Handles: [[x,y],...], [[[x,y],...]], [x,y,x,y,...], { points, holes }
 */
function getPolygonRings(poly) {
  if (!poly) return [];
  // Object shape: { points, holes? }
  if (poly.points && Array.isArray(poly.points)) {
    const rings = [poly.points];
    if (poly.holes?.length) rings.push(...poly.holes);
    return rings;
  }
  if (!Array.isArray(poly) || poly.length === 0) return [];

  const first = poly[0];
  // Already in ring format: [ [ [x,y], ... ], [ [x,y], ... ], ... ]
  if (Array.isArray(first) && first.length && Array.isArray(first[0])) {
    // Ensure numeric tuples
    return poly.map((ring) =>
      (ring ?? []).map((pt) => [Number(pt[0]), Number(pt[1])])
    );
  }

  // Flat or semi-flat numeric array: [ [x,y], ... ] or [x,y,x,y,...]
  const flat = poly.flat(2);
  if (flat.length < 4) return [];
  const points = [];
  for (let i = 0; i < flat.length - 1; i += 2) {
    points.push([Number(flat[i]), Number(flat[i + 1])]);
  }
  return [points];
}

/** Add a ring to the current path */
function addRingToPath(ctx, ring, scale, offsetX, offsetY) {
  if (!ring?.length || ring.length < 3) return;
  const [fx, fy] = ring[0];
  ctx.moveTo(offsetX + fx * scale, offsetY + fy * scale);
  for (let j = 1; j < ring.length; j++) {
    const [px, py] = ring[j];
    ctx.lineTo(offsetX + px * scale, offsetY + py * scale);
  }
  ctx.closePath();
}

/**
 * Draw a single polygon item including modifications (brush additions, eraser subtractions).
 * item can have: polygon, label, modifications: { brushAdditions, eraserSubtractions }
 */
function drawPolygonItem(ctx, item, i, scale, offsetX, offsetY, colors, options = {}) {
  const poly = item.polygon ?? item.polygons;
  const rings = getPolygonRings(poly);
  const outerPoints = rings[0];
  const color = colors[i % colors.length];
  const fillStyle = color + '20';
  const strokeStyle = color;
  const hideBasePolygon = options.hideBasePolygon === true;

  // 1. Base polygon fill (supports holes via evenodd)
  if (outerPoints?.length >= 3) {
    ctx.fillStyle = fillStyle;
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = 2;
    ctx.beginPath();
    addRingToPath(ctx, outerPoints, scale, offsetX, offsetY);
    for (let r = 1; r < rings.length; r++) {
      addRingToPath(ctx, rings[r], scale, offsetX, offsetY);
    }
    if (!hideBasePolygon) {
      ctx.fill(rings.length > 1 ? 'evenodd' : 'nonzero');
    }
    ctx.stroke();
  }

  // 2. Brush additions - extend the layer (strokes are in canvas coords, solid fill)
  const brushAdditions = item.modifications?.brushAdditions ?? [];
  brushAdditions.forEach((stroke) => {
    if (!stroke?.points?.length) return;
    const size = stroke.size ?? 10;
    ctx.fillStyle = BRUSH_STROKE_FILL;
    stroke.points.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, getToolRadius(size, 'brush'), 0, Math.PI * 2);
      ctx.fill();
    });
  });

  // 3. Eraser subtractions - cut out from the layer
  const eraserSubtractions = item.modifications?.eraserSubtractions ?? [];
  if (eraserSubtractions.length > 0) {
    ctx.globalCompositeOperation = 'destination-out';
    eraserSubtractions.forEach((stroke) => {
      if (!stroke?.points?.length) return;
      const size = stroke.size ?? 10;
      ctx.fillStyle = 'rgba(0,0,0,1)';
      stroke.points.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, getToolRadius(size, 'eraser'), 0, Math.PI * 2);
        ctx.fill();
      });
    });
    ctx.globalCompositeOperation = 'source-over';
  }

  // 4. Label
  if (!hideBasePolygon && item.label && outerPoints?.length >= 1) {
    ctx.fillStyle = color;
    ctx.font = '12px system-ui, sans-serif';
    const [tx, ty] = outerPoints[0];
    ctx.fillText(item.label, offsetX + tx * scale, offsetY + ty * scale - 4);
  }
}

/**
 * Draw polygons on canvas. taskResult items: { polygon, label, bbox, modifications?, ... }
 * editingStrokes: when editing, { strokes, currentStroke } to draw as part of the layer
 */
function drawPolygons(
  ctx,
  items,
  scale,
  offsetX,
  offsetY,
  editingStrokes,
  editingLayerIndex,
  options = {}
) {
  if (!items || !Array.isArray(items)) return;
  const colors = [
    '#3b82f6', '#ef4444', '#22c55e', '#eab308', '#8b5cf6',
    '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1',
  ];
  items.forEach((item, i) => {
    const colorIndex = items.length === 1 ? editingLayerIndex : i;
    drawPolygonItem(ctx, item, colorIndex, scale, offsetX, offsetY, colors, options);
    if (editingStrokes && (items.length === 1 || i === editingLayerIndex)) {
      drawEditingStrokes(ctx, editingStrokes);
    }
  });
}

function drawStrokeDots(ctx, points, radius) {
  points.forEach((p) => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** Draw in-progress editing strokes as part of the layer (solid brush, eraser cuts out). */
function drawEditingStrokes(ctx, { strokes, currentStroke }) {
  (strokes || []).forEach((stroke) => {
    if (!stroke?.points?.length) return;
    const size = stroke.size ?? 10;
    const radius = getToolRadius(size, stroke.tool);
    if (stroke.tool === 'brush') {
      ctx.fillStyle = BRUSH_STROKE_FILL;
      drawStrokeDots(ctx, stroke.points, radius);
    } else if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      drawStrokeDots(ctx, stroke.points, radius);
      ctx.globalCompositeOperation = 'source-over';
    }
  });

  if (!currentStroke?.points?.length) return;
  const size = currentStroke.size ?? 10;
  const radius = getToolRadius(size, currentStroke.tool);
  if (currentStroke.tool === 'brush') {
    ctx.fillStyle = BRUSH_STROKE_FILL;
    drawStrokeDots(ctx, currentStroke.points, radius);
    return;
  }
  if (currentStroke.tool === 'eraser') {
    // Keep real erase behavior, then overlay a clear red guide while dragging.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,1)';
    drawStrokeDots(ctx, currentStroke.points, radius);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(239,68,68,0.65)';
    drawStrokeDots(ctx, currentStroke.points, radius);
  }
}

/** Centroid of first ring in image coordinates [x, y], or null */
function getPolygonCentroid(poly) {
  const rings = getPolygonRings(poly);
  const outer = rings[0];
  if (!outer || outer.length < 3) return null;
  let sx = 0;
  let sy = 0;
  const toPoint = (pt) => {
    if (Array.isArray(pt)) return [Number(pt[0]) || 0, Number(pt[1]) || 0];
    return [Number(pt?.x ?? pt?.[0]) || 0, Number(pt?.y ?? pt?.[1]) || 0];
  };
  for (const p of outer) {
    const [px, py] = toPoint(p);
    sx += px;
    sy += py;
  }
  return [sx / outer.length, sy / outer.length];
}

/** Get canvas coords from pointer event (accounts for object-contain scaling) */
function getCanvasCoords(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const scale = Math.min(scaleX, scaleY);
  const offsetX = (rect.width - canvas.width / scale) / 2;
  const offsetY = (rect.height - canvas.height / scale) / 2;
  const x = (clientX - rect.left - offsetX) * scale;
  const y = (clientY - rect.top - offsetY) * scale;
  return { x, y };
}

/**
 * Points strictly after `from` along the segment to `to`, spaced by at most `step` canvas px, ending at `to`.
 */
function interpolateSegmentAfterFrom(from, to, step) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.25) return [];
  if (dist <= step) return [{ x: to.x, y: to.y }];
  const ux = dx / dist;
  const uy = dy / dist;
  const out = [];
  let d = step;
  while (d < dist - 0.25) {
    out.push({ x: from.x + ux * d, y: from.y + uy * d });
    d += step;
  }
  out.push({ x: to.x, y: to.y });
  return out;
}

function pointInRingCanvas(cx, cy, ringCanvas) {
  if (!ringCanvas || ringCanvas.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ringCanvas.length - 1; i < ringCanvas.length; j = i++) {
    const [xi, yi] = ringCanvas[i];
    const [xj, yj] = ringCanvas[j];
    const intersect =
      yi > cy !== yj > cy &&
      cx < ((xj - xi) * (cy - yi)) / (yj - yi + 1e-10) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInPolygonCanvas(cx, cy, ringsCanvas) {
  let count = 0;
  for (const ring of ringsCanvas) {
    if (ring.length >= 3 && pointInRingCanvas(cx, cy, ring)) count += 1;
  }
  return count % 2 === 1;
}

function getHitPolygonIndex(polygons, testOrder, x, y, scale, tolerance = 6) {
  if (!Array.isArray(polygons) || polygons.length === 0) return null;
  const toPoint = (pt) => {
    if (Array.isArray(pt)) return [Number(pt[0]) || 0, Number(pt[1]) || 0];
    return [Number(pt?.x ?? pt?.[0]) || 0, Number(pt?.y ?? pt?.[1]) || 0];
  };
  const toTry = [
    [x, y],
    [x + tolerance, y],
    [x - tolerance, y],
    [x, y + tolerance],
    [x, y - tolerance],
    [x + tolerance, y + tolerance],
    [x - tolerance, y - tolerance],
    [x + tolerance, y - tolerance],
    [x - tolerance, y + tolerance],
  ];
  for (const i of testOrder) {
    const item = polygons[i];
    const poly = item?.polygon ?? item?.polygons;
    const rings = getPolygonRings(poly);
    if (rings.length === 0 || rings.every((r) => (r ?? []).length < 3)) continue;
    const ringsCanvas = rings.map((ring) =>
      (ring ?? []).map((pt) => {
        const [px, py] = toPoint(pt);
        return [px * scale, py * scale];
      })
    );
    const hit = toTry.some(([cx, cy]) => pointInPolygonCanvas(cx, cy, ringsCanvas));
    if (hit) return i;
  }
  return null;
}

function getPolygonTooltipLabel(item, index, getPolygonLabel) {
  if (typeof getPolygonLabel === 'function') {
    const resolved = getPolygonLabel(item, index);
    if (resolved != null && String(resolved).trim() !== '') return String(resolved).trim();
  }
  const byLabel = item?.label;
  if (byLabel != null && String(byLabel).trim() !== '') return String(byLabel).trim();
  const byNestedType =
    item?.interiorType?.interiorType ??
    item?.interior_type?.interiorType ??
    item?.interiorTypeName ??
    item?.interior_type_name;
  if (byNestedType != null && String(byNestedType).trim() !== '') return String(byNestedType).trim();
  return `Object ${index + 1}`;
}

/**
 * Canvas that draws image + polygon overlay.
 * Polygons are in image pixel coordinates.
 * editingLayerIndex: when set, only draw that single polygon (others hidden).
 * Edit mode: activeTool, brushSize, zoom, pan, strokes, onStrokesChange.
 */
function ImageWithPolygons({
  image,
  polygons,
  isLoading,
  editingLayerIndex,
  selectedPolygonIndex = null,
  showAllPolygonsOnCanvas = false,
  /** When set (non-empty), draw only these polygon indices (image coords). Overrides show-all / single selection. */
  visiblePolygonIndices = null,
  /** 0–1 opacity for polygon overlay when not in layer edit mode (image stays opaque). */
  polygonOverlayOpacity = 1,
  onPolygonClick,
  onPolygonBrushClick,
  onPolygonRemoveClick = null,
  polygonActionType = 'brush',
  activeTool = 'brush',
  magicMode = 'add',
  onMagicClick,
  brushSize = 10,
  zoom = 1,
  pan = { x: 0, y: 0 },
  strokes = [],
  onStrokesChange,
  /** When editing, called on wheel with a non-passive listener so parent can preventDefault and zoom. */
  onEditingWheel = null,
  className = '',
  getPolygonLabel,
  // Magic wand click prompts (in image pixel coords) to visualize on the canvas.
  clickPoints = [],
  showBottomActions = false,
  bottomActionsAnchor = null,
  onApplyFinish,
  onRemoveFromList,
  onRefine,
  onRenameObject,
  onCloseBottomActions,
  isMultiSelectActive = false,
  showOpacityControl = true,
  onPolygonOverlayOpacityChange,
  hideSelectionActionButtons = false,
  hideEditingPolygonOverlay = false,
  /** When set (e.g. POST /rooms/…/images width/height), use for canvas + coords so local file size ≠ server image size. */
  referenceImagePixelSize = null,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const bottomActionsToolbarRef = useRef(null);
  /** Horizontal nudge (px) so anchored bottom-actions bar stays inside the canvas container. */
  const [bottomActionsShiftPx, setBottomActionsShiftPx] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ cw: 0, ch: 0 });
  const scaleRef = useRef(1);
  const [cursorPos, setCursorPos] = useState(null);
  const [cursorScale, setCursorScale] = useState(1);
  const [hoveredPolygon, setHoveredPolygon] = useState(null); // { label, x, y }
  const polygonTooltipRef = useRef(null);
  /** Pixel left/top inside container so the label stays inside the canvas box */
  const [polygonTooltipPos, setPolygonTooltipPos] = useState(null);
  const [currentStroke, setCurrentStroke] = useState(null);
  const currentStrokeRef = useRef(null);
  currentStrokeRef.current = currentStroke;
  const isDrawingRef = useRef(false);
  const panStartRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = image;
    if (!canvas || !img?.complete) return;

    const ref = referenceImagePixelSize;
    const useRefSize =
      ref &&
      Number(ref.width) > 0 &&
      Number(ref.height) > 0;
    const nw = useRefSize
      ? Number(ref.width)
      : img.naturalWidth || img.width;
    const nh = useRefSize
      ? Number(ref.height)
      : img.naturalHeight || img.height;
    if (!nw || !nh) return;

    let cw = nw;
    let ch = nh;
    if (Math.max(nw, nh) > MAX_CANVAS_SIZE) {
      const ratio = MAX_CANVAS_SIZE / Math.max(nw, nh);
      cw = Math.round(nw * ratio);
      ch = Math.round(nh * ratio);
    }

    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
    }
    const scale = cw / nw;
    scaleRef.current = scale;
    setCanvasSize((prev) => (prev.cw === cw && prev.ch === ch ? prev : { cw, ch }));

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);

    if (polygons && polygons.length > 0) {
      const hasEditing =
        editingLayerIndex != null &&
        editingLayerIndex >= 0 &&
        editingLayerIndex < polygons.length;

      // When editing, always draw the editing layer only.
      if (hasEditing) {
        const toDraw = [polygons[editingLayerIndex]];
        const layerIndex = editingLayerIndex;
        const editingStrokes = { strokes, currentStroke };
        drawPolygons(ctx, toDraw, scale, 0, 0, editingStrokes, layerIndex, {
          hideBasePolygon: hideEditingPolygonOverlay,
        });
      } else {
        const alpha =
          typeof polygonOverlayOpacity === 'number' && Number.isFinite(polygonOverlayOpacity)
            ? Math.min(1, Math.max(0, polygonOverlayOpacity))
            : 1;
        const withOverlayAlpha = (fn) => {
          if (alpha >= 0.999) {
            fn();
            return;
          }
          ctx.save();
          ctx.globalAlpha = alpha;
          fn();
          ctx.restore();
        };
        if (
          visiblePolygonIndices != null &&
          visiblePolygonIndices.length > 0
        ) {
          const colors = [
            '#3b82f6', '#ef4444', '#22c55e', '#eab308', '#8b5cf6',
            '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1',
          ];
          withOverlayAlpha(() => {
            visiblePolygonIndices.forEach((layerIndex) => {
              const item = polygons[layerIndex];
              if (item) drawPolygonItem(ctx, item, layerIndex, scale, 0, 0, colors);
            });
          });
        } else if (showAllPolygonsOnCanvas) {
          withOverlayAlpha(() => {
            drawPolygons(ctx, polygons, scale, 0, 0, null, null);
          });
        } else if (
          selectedPolygonIndex != null &&
          selectedPolygonIndex >= 0 &&
          selectedPolygonIndex < polygons.length
        ) {
          withOverlayAlpha(() => {
            const toDraw = [polygons[selectedPolygonIndex]];
            const layerIndex = selectedPolygonIndex;
            drawPolygons(ctx, toDraw, scale, 0, 0, null, layerIndex);
          });
        }
      }
      // Otherwise (no editing, no selection, not show-all), we hide polygons on the canvas.
    }

    // Draw SAM magic wand click points (in image coords) for visual feedback.
    if (activeTool === 'magicwand' && clickPoints && clickPoints.length > 0) {
      ctx.save();
      ctx.lineWidth = 2;
      clickPoints.forEach(({ x, y, label }) => {
        const cx = x * scale;
        const cy = y * scale;
        const isPositive =
          label === 1 ||
          label === 'add' ||
          label === 'positive' ||
          label === true;
        // Positive points: green, negative: red.
        const strokeColor = isPositive ? '#22c55e' : '#ef4444';
        const fillColor = isPositive ? '#22c55e55' : '#ef444455';
        const radius = 6;

        // Outer circle
        ctx.beginPath();
        ctx.strokeStyle = strokeColor;
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();

        // Inner dot
        ctx.beginPath();
        ctx.fillStyle = fillColor;
        ctx.arc(cx, cy, radius / 2, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }
  }, [
    image,
    polygons,
    editingLayerIndex,
    strokes,
    currentStroke,
    selectedPolygonIndex,
    showAllPolygonsOnCanvas,
    visiblePolygonIndices,
    polygonOverlayOpacity,
    activeTool,
    clickPoints,
    referenceImagePixelSize,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  useLayoutEffect(() => {
    if (!showBottomActions || selectedPolygonIndex == null || !bottomActionsAnchor) {
      setBottomActionsShiftPx(0);
      return undefined;
    }
    const container = containerRef.current;
    const toolbar = bottomActionsToolbarRef.current;
    if (!container || !toolbar) return undefined;

    const measureAndClamp = () => {
      const cw = container.clientWidth;
      if (cw <= 0) return;
      const tw = toolbar.offsetWidth;
      if (tw <= 0) return;
      const leftPct = Number(bottomActionsAnchor.leftPct);
      if (!Number.isFinite(leftPct)) return;
      const center = (leftPct / 100) * cw;
      const pad = 6;
      if (tw >= cw - pad * 2) {
        setBottomActionsShiftPx(cw / 2 - center);
        return;
      }
      let shift = 0;
      let leftEdge = center - tw / 2 + shift;
      let rightEdge = center + tw / 2 + shift;
      if (leftEdge < pad) shift += pad - leftEdge;
      leftEdge = center - tw / 2 + shift;
      rightEdge = center + tw / 2 + shift;
      if (rightEdge > cw - pad) shift -= rightEdge - (cw - pad);
      setBottomActionsShiftPx(shift);
    };

    measureAndClamp();
    const ro = new ResizeObserver(() => {
      measureAndClamp();
    });
    ro.observe(container);
    ro.observe(toolbar);
    return () => ro.disconnect();
  }, [
    showBottomActions,
    bottomActionsAnchor,
    selectedPolygonIndex,
    showOpacityControl,
    polygonOverlayOpacity,
    canvasSize.cw,
    canvasSize.ch,
  ]);

  const isEditing = editingLayerIndex != null;

  useLayoutEffect(() => {
    if (!hoveredPolygon || isEditing) {
      setPolygonTooltipPos(null);
      return;
    }
    const cont = containerRef.current;
    const tip = polygonTooltipRef.current;
    if (!cont || !tip || canvasSize.cw <= 0 || canvasSize.ch <= 0) {
      setPolygonTooltipPos(null);
      return;
    }
    const pad = 6;
    const offsetX = 8;
    const offsetY = -24;
    const cw = canvasSize.cw;
    const ch = canvasSize.ch;

    let rafAttempts = 0;
    const measureAndClamp = () => {
      const tw = tip.offsetWidth;
      const th = tip.offsetHeight;
      const cwPx = cont.clientWidth;
      const chPx = cont.clientHeight;
      if ((tw < 1 || th < 1 || cwPx < 1 || chPx < 1) && rafAttempts < 8) {
        rafAttempts += 1;
        requestAnimationFrame(measureAndClamp);
        return;
      }
      if (tw < 1 || th < 1 || cwPx < 1 || chPx < 1) return;
      let leftPx = (hoveredPolygon.x / cw) * cwPx + offsetX;
      let topPx = (hoveredPolygon.y / ch) * chPx + offsetY;
      const maxLeft = Math.max(pad, cwPx - tw - pad);
      const maxTop = Math.max(pad, chPx - th - pad);
      leftPx = Math.min(Math.max(leftPx, pad), maxLeft);
      topPx = Math.min(Math.max(topPx, pad), maxTop);
      setPolygonTooltipPos({ left: leftPx, top: topPx });
    };
    measureAndClamp();

    const ro = new ResizeObserver(() => {
      measureAndClamp();
    });
    ro.observe(cont);
    return () => ro.disconnect();
  }, [hoveredPolygon, isEditing, canvasSize.cw, canvasSize.ch]);

  const getCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return getCanvasCoords(canvas, e.clientX, e.clientY);
  }, []);

  const handlePointerDown = useCallback(
    (e) => {
      const coords = getCoords(e);
      if (!coords || canvasSize.cw === 0) return;
      const { x, y } = coords;
      if (x < 0 || x > canvasSize.cw || y < 0 || y > canvasSize.ch) return;

      if (activeTool === 'magicwand') {
        const scale = scaleRef.current || 1;
        const imgX = x / scale;
        const imgY = y / scale;
        onMagicClick?.({ x: imgX, y: imgY, label: magicMode });
        return;
      }
      if (activeTool === 'move') {
        panStartRef.current = { x: e.clientX, y: e.clientY };
      } else if (activeTool === 'brush' || activeTool === 'eraser') {
        isDrawingRef.current = true;
        setCurrentStroke({ tool: activeTool, size: brushSize, points: [{ x, y }] });
      }
    },
    [activeTool, brushSize, canvasSize, getCoords, magicMode, onMagicClick]
  );

  const handlePointerMove = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      const coords = getCoords(e);
      if (!coords) return;
      const { x, y } = coords;

      if (!isEditing) {
        const scale = scaleRef.current || 1;
        const hasSelected =
          selectedPolygonIndex != null &&
          selectedPolygonIndex >= 0 &&
          selectedPolygonIndex < (polygons?.length ?? 0);
        const hasVisibleSubset =
          visiblePolygonIndices != null &&
          visiblePolygonIndices.length > 0;
        const visibleIndices = hasVisibleSubset
          ? visiblePolygonIndices
          : showAllPolygonsOnCanvas
            ? (polygons ?? []).map((_, i) => i)
            : hasSelected
              ? [selectedPolygonIndex]
              : [];
        if (visibleIndices.length === 0) {
          setHoveredPolygon(null);
        } else {
          const testOrder = [...visibleIndices].reverse();
          const hitIndex = getHitPolygonIndex(polygons, testOrder, x, y, scale);
          if (hitIndex == null) {
            setHoveredPolygon(null);
          } else {
            const item = polygons[hitIndex] ?? {};
            setHoveredPolygon({
              label: getPolygonTooltipLabel(item, hitIndex, getPolygonLabel),
              x,
              y,
            });
          }
        }
      }

      if (canvas && (activeTool === 'brush' || activeTool === 'eraser')) {
        const rect = canvas.getBoundingClientRect();
        const scale = Math.min(rect.width / canvas.width, rect.height / canvas.height) || 1;
        setCursorPos({ x: e.clientX, y: e.clientY });
        setCursorScale(scale);
      }
      if (activeTool === 'move' && panStartRef.current) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        onStrokesChange?.({ type: 'pan', dx, dy });
        return;
      }

      if (!isDrawingRef.current || !canvas) return;
      const native = e.nativeEvent;
      let coalesced = [native ?? e];
      if (native && typeof native.getCoalescedEvents === 'function') {
        const batch = native.getCoalescedEvents();
        if (batch && batch.length > 0) coalesced = batch;
      }
      setCurrentStroke((prev) => {
        if (!prev || (prev.tool !== 'brush' && prev.tool !== 'eraser')) return prev;
        let points = prev.points;
        if (!points?.length) return prev;
        const radius = getToolRadius(prev.size ?? 10, prev.tool);
        const step = Math.max(1, radius * BRUSH_INTERPOLATE_STEP_FACTOR);
        for (const ev of coalesced) {
          if (!ev) continue;
          const c = getCanvasCoords(canvas, ev.clientX, ev.clientY);
          if (!c) continue;
          const from = points[points.length - 1];
          const extra = interpolateSegmentAfterFrom(from, c, step);
          if (extra.length) points = [...points, ...extra];
        }
        return { ...prev, points };
      });
    },
    [activeTool, getCoords, isEditing, onStrokesChange, polygons, selectedPolygonIndex, showAllPolygonsOnCanvas, visiblePolygonIndices]
  );

  const handlePointerLeave = useCallback(() => {
    setCursorPos(null);
    setHoveredPolygon(null);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (panStartRef.current) {
      panStartRef.current = null;
      return;
    }
    const stroke = currentStrokeRef.current;
    if (isDrawingRef.current && stroke && stroke.points.length > 0) {
      onStrokesChange?.({ type: 'stroke-end', stroke });
      setCurrentStroke(null);
    }
    isDrawingRef.current = false;
  }, [onStrokesChange]);
  const handleCanvasClick = useCallback(
    (e) => {
      if (!onPolygonClick || !polygons || polygons.length === 0) return;
      if (isEditing) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { x, y } = getCanvasCoords(canvas, e.clientX, e.clientY);
      const scale = scaleRef.current || 1;
      console.log('[Canvas click] coords:', { x: Math.round(x), y: Math.round(y) }, 'polygons:', polygons.length);

      const order = polygons.map((_, i) => i).reverse();
      const hitIndex = getHitPolygonIndex(polygons, order, x, y, scale);
      if (hitIndex != null) {
        const item = polygons[hitIndex];
        console.log('[Canvas click] Polygon active:', {
          index: hitIndex,
          interior_type_id: item?.interior_type_id,
          label: item?.label ?? '(no label)',
        });
        onPolygonClick(hitIndex);
      }
    },
    [onPolygonClick, polygons, isEditing]
  );

  useEffect(() => {
    if (!isEditing) return;
    const onUp = () => handlePointerUp();
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointerleave', onUp);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointerleave', onUp);
    };
  }, [isEditing, handlePointerUp]);

  useEffect(() => {
    const el = containerRef.current;
    if (!isEditing || !onEditingWheel || !el) return;
    const onWheel = (ev) => onEditingWheel(ev);
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isEditing, onEditingWheel]);

  if (!image) return null;

  const polygonActionCallback = polygonActionType === 'remove' ? onPolygonRemoveClick : onPolygonBrushClick;
  const showBrushButtons =
    !isEditing &&
    polygonActionType !== 'remove' &&
    polygonActionCallback &&
    polygons &&
    polygons.length > 0 &&
    canvasSize.cw > 0 &&
    canvasSize.ch > 0 &&
    !showBottomActions &&
    ((visiblePolygonIndices != null && visiblePolygonIndices.length > 0) ||
      showAllPolygonsOnCanvas ||
      (selectedPolygonIndex != null && selectedPolygonIndex >= 0 && selectedPolygonIndex < polygons.length));

  const refPx = referenceImagePixelSize;
  const logicalNw =
    refPx && Number(refPx.width) > 0 && Number(refPx.height) > 0
      ? Number(refPx.width)
      : image?.naturalWidth || image?.width || 0;
  const logicalNh =
    refPx && Number(refPx.width) > 0 && Number(refPx.height) > 0
      ? Number(refPx.height)
      : image?.naturalHeight || image?.height || 0;
  const scaleToCanvas =
    logicalNw > 0 ? (canvasSize.cw || 0) / logicalNw : 1;

  const brushButtonPolygons = showBrushButtons
    ? visiblePolygonIndices != null && visiblePolygonIndices.length > 0
      ? visiblePolygonIndices.map((i) => polygons[i])
      : showAllPolygonsOnCanvas
        ? polygons
        : [polygons[selectedPolygonIndex]]
    : [];
  const brushButtonIndices = showBrushButtons
    ? visiblePolygonIndices != null && visiblePolygonIndices.length > 0
      ? visiblePolygonIndices
      : showAllPolygonsOnCanvas
        ? polygons.map((_, i) => i)
        : [selectedPolygonIndex]
    : [];

  return (
    <div
      ref={containerRef}
      className={`relative mx-auto block w-fit max-h-full max-w-full min-h-0 min-w-0 ${className}`}
      style={isEditing ? { overflow: 'hidden' } : undefined}
    >
      <canvas
        ref={canvasRef}
        className={`block max-h-full max-w-full object-contain rounded ${isEditing
            ? activeTool === 'move'
              ? 'cursor-grab'
              : activeTool === 'brush' || activeTool === 'eraser'
                ? 'cursor-none'
                : 'cursor-crosshair'
            : ''
          }`}
        style={
          isEditing && (zoom !== 1 || pan.x !== 0 || pan.y !== 0)
            ? {
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
              }
            : undefined
        }
        onPointerDown={isEditing ? handlePointerDown : undefined}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onClick={handleCanvasClick}
      />
      {!isEditing && hoveredPolygon && (
        <div
          ref={polygonTooltipRef}
          className="pointer-events-none absolute z-20 px-2 py-1 text-xs font-medium text-white bg-gray-900/90 rounded shadow-sm max-w-[min(18rem,calc(100%-12px))] truncate"
          style={
            polygonTooltipPos
              ? {
                  left: polygonTooltipPos.left,
                  top: polygonTooltipPos.top,
                }
              : {
                  left: `${(hoveredPolygon.x / (canvasSize.cw || 1)) * 100}%`,
                  top: `${(hoveredPolygon.y / (canvasSize.ch || 1)) * 100}%`,
                  transform: 'translate(8px, -24px)',
                }
          }
          aria-hidden
        >
          {hoveredPolygon.label}
        </div>
      )}
      {showBrushButtons && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ width: '100%', height: '100%' }}
          aria-hidden
        >
          {brushButtonPolygons.map((item, idx) => {
            const poly = item?.polygon ?? item?.polygons;
            const centroid = getPolygonCentroid(poly);
            const index = brushButtonIndices[idx];
            if (centroid == null || index == null) return null;
            const [ix, iy] = centroid;
            const cx = ix * scaleToCanvas;
            const cy = iy * scaleToCanvas;
            const leftPct = (cx / canvasSize.cw) * 100;
            const topPct = (cy / canvasSize.ch) * 100;
            return (
              <button
                key={index}
                type="button"
                className="absolute w-8 h-8 rounded-full bg-gray-800 text-white shadow-lg flex items-center justify-center pointer-events-auto hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-600"
                style={{
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (polygonActionType === 'remove') {
                    onPolygonRemoveClick?.(index);
                  } else {
                    onPolygonBrushClick?.(index, { leftPct, topPct });
                  }
                }}
                aria-label={polygonActionType === 'remove' ? `Remove polygon ${index + 1}` : `Edit polygon ${index + 1}`}
              >
                {polygonActionType === 'remove' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
      {showBottomActions && selectedPolygonIndex != null && (
        <div
          className={`pointer-events-none absolute z-[25] flex justify-center px-2 ${
            bottomActionsAnchor
              ? ''
              : 'bottom-3 left-1/2 w-full max-w-[min(100%,36rem)] -translate-x-1/2'
          }`}
          style={
            bottomActionsAnchor
              ? {
                  left: `${bottomActionsAnchor.leftPct}%`,
                  top: `${bottomActionsAnchor.topPct}%`,
                  transform: `translate(calc(-50% + ${bottomActionsShiftPx}px), calc(16px + 0.5rem))`,
                  maxWidth: 'calc(100% - 12px)',
                }
              : undefined
          }
        >
          <div
            ref={bottomActionsToolbarRef}
            className="pointer-events-auto flex max-w-full flex-nowrap items-center justify-center gap-1.5 overflow-x-auto overscroll-x-contain rounded-[26px] py-2 px-2 sm:px-3 transition-colors backdrop-blur-xl backdrop-saturate-150 border-white/50 bg-white/35 [scrollbar-width:thin]"
          >
            {showOpacityControl && (
              <div className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 sm:px-2.5 rounded-full bg-white border border-gray-200 shadow-sm">
                <label
                  htmlFor="photorealistic-polygon-opacity"
                  className="text-[10px] font-medium text-gray-600 whitespace-nowrap shrink-0"
                >
                  Opacity
                </label>
                <input
                  id="photorealistic-polygon-opacity"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(polygonOverlayOpacity * 100)}
                  onChange={(e) => onPolygonOverlayOpacityChange?.(Number(e.target.value) / 100)}
                  className="w-[4.5rem] sm:w-24 h-1.5 accent-gray-800 cursor-pointer"
                  title="Polygon overlay opacity on canvas"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(polygonOverlayOpacity * 100)}
                />
                {/*
                <span className="tabular-nums text-[10px] font-medium text-gray-600 w-7 text-right shrink-0">
                  {Math.round(polygonOverlayOpacity * 100)}%
                </span>*/}
              </div>
            )}
            {!hideSelectionActionButtons && (
              <button
                type="button"
                onClick={onApplyFinish}
                className="shrink-0 p-2 rounded-full bg-white text-primary-300 hover:bg-primary-300 hover:text-white transition-colors"
                title="Apply finish (open materials)"
                aria-label="Apply finish (open materials)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 17 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M12.75 3.25H13.75C14.855 3.25 15.75 3.645 15.75 4.75V6.75C15.75 7.28043 15.5393 7.78914 15.1642 8.16421C14.7891 8.53929 14.2804 8.75 13.75 8.75H6.75C6.48478 8.75 6.23043 8.85536 6.04289 9.04289C5.85536 9.23043 5.75 9.48478 5.75 9.75V17.75C5.75 18.0152 5.85536 18.2696 6.04289 18.4571C6.23043 18.6446 6.48478 18.75 6.75 18.75H7.75M12.75 3.25V1.75C12.75 1.48478 12.6446 1.23043 12.4571 1.04289C12.2696 0.855357 12.0152 0.75 11.75 0.75H1.75C1.48478 0.75 1.23043 0.855357 1.04289 1.04289C0.855357 1.23043 0.75 1.48478 0.75 1.75V4.75C0.75 5.01522 0.855357 5.26957 1.04289 5.45711C1.23043 5.64464 1.48478 5.75 1.75 5.75H11.75C12.0152 5.75 12.2696 5.64464 12.4571 5.45711C12.6446 5.26957 12.75 5.01522 12.75 4.75V3.25Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onRefine}
              className="shrink-0 p-2 rounded-full bg-white text-primary-300 hover:bg-primary-300 hover:text-white transition-colors"
              title="Refine (open editor toolbar)"
              aria-label="Refine (open editor toolbar)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 17 17" fill="currentColor" aria-hidden>
                <path d="M14.3 1.65C14.3 2.08761 14.4738 2.50729 14.7833 2.81673C15.0927 3.12616 15.5124 3.3 15.95 3.3C16.3876 3.3 16.8073 3.12616 17.1167 2.81673C17.4262 2.50729 17.6 2.08761 17.6 1.65C17.6 1.21239 17.4262 0.792709 17.1167 0.483274C16.8073 0.173839 16.3876 0 15.95 0C15.5124 0 15.0927 0.173839 14.7833 0.483274C14.4738 0.792709 14.3 1.21239 14.3 1.65ZM0 13.75C0 13.9667 0.0426786 14.1812 0.125599 14.3814C0.208519 14.5816 0.330057 14.7635 0.483274 14.9167C0.636491 15.0699 0.818385 15.1915 1.01857 15.2744C1.21876 15.3573 1.43332 15.4 1.65 15.4C1.86668 15.4 2.08124 15.3573 2.28143 15.2744C2.48162 15.1915 2.66351 15.0699 2.81673 14.9167C2.96994 14.7635 3.09148 14.5816 3.1744 14.3814C3.25732 14.1812 3.3 13.9667 3.3 13.75C3.3 13.3124 3.12616 12.8927 2.81673 12.5833C2.50729 12.2738 2.08761 12.1 1.65 12.1C1.21239 12.1 0.792709 12.2738 0.483274 12.5833C0.173839 12.8927 0 13.3124 0 13.75ZM4.4 1.65C4.4 2.08761 4.57384 2.50729 4.88327 2.81673C5.19271 3.12616 5.61239 3.3 6.05 3.3C6.48761 3.3 6.90729 3.12616 7.21673 2.81673C7.52616 2.50729 7.7 2.08761 7.7 1.65C7.7 1.21239 7.52616 0.792709 7.21673 0.483274C6.90729 0.173839 6.48761 0 6.05 0C5.61239 0 5.19271 0.173839 4.88327 0.483274C4.57384 0.792709 4.4 1.21239 4.4 1.65ZM8.6746 2.475H13.3265C13.1576 1.93797 13.1576 1.36203 13.3265 0.825H8.6735C8.84239 1.36203 8.84349 1.93797 8.6746 2.475ZM1.771 11.0022L4.378 3.8335C4.8114 4.1657 5.346 4.3725 5.929 4.3978L3.322 11.5665C2.87497 11.224 2.33358 11.0271 1.771 11.0022ZM4.2746 14.575H8.1488L8.1752 14.4705C8.3039 13.9568 8.5459 13.4794 8.8836 13.0735C8.8968 13.0236 8.9111 12.9741 8.9265 12.925H4.2735C4.44239 13.462 4.44349 14.038 4.2746 14.575ZM15.829 4.3978L14.817 7.183C14.6732 7.28564 14.5378 7.39968 14.4122 7.524L12.0923 9.845L14.278 3.8335C14.7114 4.1657 15.246 4.3725 15.829 4.3978ZM9.878 13.6158L15.191 8.3028C15.3815 8.1094 15.6084 7.95562 15.8586 7.85032C16.1088 7.74503 16.3774 7.6903 16.6489 7.6893C16.9204 7.6883 17.1893 7.74105 17.4403 7.8445C17.6913 7.94794 17.9193 8.10005 18.1113 8.29204C18.3032 8.48404 18.4552 8.71212 18.5586 8.96315C18.6619 9.21417 18.7146 9.48317 18.7135 9.75464C18.7123 10.0261 18.6575 10.2947 18.5521 10.5449C18.4467 10.795 18.2929 11.0219 18.0994 11.2123L12.7864 16.5242C12.4768 16.8337 12.089 17.0535 11.6644 17.16L10.0166 17.5714C9.85275 17.6122 9.68113 17.61 9.5184 17.5649C9.35568 17.5198 9.20739 17.4334 9.08793 17.314C8.96847 17.1947 8.88192 17.0465 8.83667 16.8838C8.79142 16.7211 8.78902 16.5495 8.8297 16.3856L9.2422 14.7378C9.34875 14.3132 9.56848 13.9254 9.878 13.6158Z"/>
              </svg>
            </button>
            {!hideSelectionActionButtons && (
              <button
                type="button"
                onClick={onRenameObject}
                className="shrink-0 p-2 rounded-full bg-white text-primary-300 hover:bg-primary-300 hover:text-white transition-colors"
                title="Rename object"
                aria-label="Rename object"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 15 13" fill="currentColor" stroke="currentColor" strokeWidth="0.05" aria-hidden="true">
                  <path d="M1.58559 12.6847C1.14955 12.6847 0.776408 12.5296 0.466162 12.2193C0.155916 11.9091 0.000528529 11.5357 0 11.0991V1.58559C0 1.14955 0.155387 0.776408 0.466162 0.466162C0.776937 0.155916 1.15008 0.000528529 1.58559 0H10.3063C10.5574 0 10.7952 0.0562881 11.0198 0.168865C11.2444 0.281441 11.4294 0.436565 11.5748 0.634234L15.1423 5.39099C15.3538 5.66847 15.4595 5.98559 15.4595 6.34234C15.4595 6.6991 15.3538 7.01622 15.1423 7.29369L11.5748 12.0505C11.4294 12.2486 11.2444 12.404 11.0198 12.5166C10.7952 12.6292 10.5574 12.6852 10.3063 12.6847H1.58559ZM1.58559 11.0991H10.3063L13.8739 6.34234L10.3063 1.58559H1.58559V11.0991Z" />
                </svg>
              </button>
            )}
            {!hideSelectionActionButtons && (
              <button
                type="button"
                onClick={onRemoveFromList}
                className="shrink-0 p-2 rounded-full bg-white text-primary-300 hover:bg-primary-300 hover:text-white transition-colors"
                title="Remove from detected object list"
                aria-label="Remove from detected object list"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onCloseBottomActions}
              className="shrink-0 p-2 rounded-full bg-white text-primary-300 hover:bg-primary-300 hover:text-white transition-colors"
              title={isMultiSelectActive ? 'Close actions' : 'Cancel selection'}
              aria-label={isMultiSelectActive ? 'Close actions' : 'Cancel selection'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {isEditing &&
        cursorPos &&
        (activeTool === 'brush' || activeTool === 'eraser') && (
          <div
            className="pointer-events-none fixed z-[9999]"
            style={{
              left: cursorPos.x,
              top: cursorPos.y,
              transform: 'translate(-50%, -50%)',
              width:
                (activeTool === 'eraser'
                  ? getToolRadius(brushSize, 'eraser') * 2
                  : brushSize) * cursorScale,
              height:
                (activeTool === 'eraser'
                  ? getToolRadius(brushSize, 'eraser') * 2
                  : brushSize) * cursorScale,
            }}
          >
            {activeTool === 'brush' ? (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle
                  cx="50"
                  cy="50"
                  r="48"
                  fill="none"
                  stroke="rgba(0,0,0,0.7)"
                  strokeWidth="3"
                />
                <line
                  x1="50"
                  y1="25"
                  x2="50"
                  y2="75"
                  stroke="rgba(0,0,0,0.7)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <line
                  x1="25"
                  y1="50"
                  x2="75"
                  y2="50"
                  stroke="rgba(0,0,0,0.7)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle
                  cx="50"
                  cy="50"
                  r="48"
                  fill="none"
                  stroke="rgba(0,0,0,0.7)"
                  strokeWidth="3"
                />
                <line
                  x1="25"
                  y1="50"
                  x2="75"
                  y2="50"
                  stroke="rgba(0,0,0,0.7)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        )}
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/40 rounded"
          aria-label="Preparing image"
        >
          <div className="flex flex-col items-center gap-2 text-white">
            <svg
              className="animate-spin h-8 w-8"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm font-medium">Preparing image...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageWithPolygons;
